import { useCallback } from 'react';

import { UPDATE_LOCK_RELEASE_MS } from '../constants/editor';
import { MarkdownTipTapConverter } from '../converters/MarkdownTipTapConverter';
import type { ExtendedEditor } from '../types/editor';
import { createLogger } from '../utils/logger';
import { isValidUrl } from '../utils/security';

const logger = createLogger('useMarkdownInsertion');

interface UseMarkdownInsertionOptions {
  editor: ExtendedEditor | null;
  onImageSourceSelect?: (file: File) => string | Promise<string>;
  publicImagePathPrefix?: string;
  setIsUpdating: (value: boolean) => void;
}

export const useMarkdownInsertion = ({
  editor,
  publicImagePathPrefix,
  setIsUpdating,
}: UseMarkdownInsertionOptions) => {
  const insertPlainText = useCallback(
    (text: string) => {
      if (!editor) {
        return;
      }

      const lines = text.split('\n');
      if (lines.length === 1) {
        // For single line, insert as plain text to keep it inline
        editor.commands.insertContent({ type: 'text', text: lines[0] });
        return;
      }

      const content = lines.map((line) => ({
        type: 'paragraph',
        content: line.length > 0 ? [{ type: 'text', text: line }] : [],
      }));
      editor.commands.insertContent(content);
    },
    [editor],
  );

  const handleInsertMarkdown = useCallback(
    async (markdown: string, cursorOffset?: number) => {
      if (!editor) return;

      logger.debug(`🎨 Inserting markdown: "${markdown}"`);

      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, ' ').trim();

      if (markdown === '****') {
        editor.chain().toggleBold().focus(undefined, { scrollIntoView: false }).run();
        return;
      }

      if (markdown === '**') {
        editor.chain().toggleItalic().focus(undefined, { scrollIntoView: false }).run();
        return;
      }

      if (markdown === '~~~~') {
        editor.chain().toggleStrike().focus(undefined, { scrollIntoView: false }).run();
        return;
      }

      if (markdown === '``') {
        editor.chain().toggleCode().focus(undefined, { scrollIntoView: false }).run();
        return;
      }

      if (markdown.match(/^#+\s$/)) {
        const level = markdown.trim().length as 1 | 2 | 3 | 4 | 5 | 6;
        logger.debug(`🎯 Heading: level=${level}, selectedText="${selectedText}"`);
        if (selectedText) {
          const result = editor
            .chain()
            .deleteRange({ from, to })
            .insertContent({
              type: 'heading',
              attrs: { level },
              content: [{ type: 'text', text: selectedText }],
            })
            .focus(undefined, { scrollIntoView: false })
            .run();
          logger.debug(`✅ Heading insertion result: ${result}`);
        } else {
          const result = editor
            .chain()
            .setHeading({ level })
            .focus(undefined, { scrollIntoView: false })
            .run();
          logger.debug(`✅ Set heading result: ${result}`);
        }
        return;
      }

      if (markdown === '- ') {
        logger.debug(`🎯 Bullet list: selectedText="${selectedText}"`);
        if (selectedText) {
          const result = editor
            .chain()
            .deleteRange({ from, to })
            .insertContent({
              type: 'bulletList',
              content: [
                {
                  type: 'listItem',
                  content: [{ type: 'paragraph', content: [{ type: 'text', text: selectedText }] }],
                },
              ],
            })
            .focus(undefined, { scrollIntoView: false })
            .run();
          logger.debug(`✅ Bullet list insertion result: ${result}`);
        } else {
          const result = editor
            .chain()
            .toggleBulletList()
            .focus(undefined, { scrollIntoView: false })
            .run();
          logger.debug(`✅ Toggle bullet list result: ${result}`);
        }
        return;
      }

      if (markdown.match(/^\d+\.\s$/)) {
        if (selectedText) {
          editor
            .chain()
            .deleteRange({ from, to })
            .insertContent({
              type: 'orderedList',
              content: [
                {
                  type: 'listItem',
                  content: [{ type: 'paragraph', content: [{ type: 'text', text: selectedText }] }],
                },
              ],
            })
            .focus(undefined, { scrollIntoView: false })
            .run();
        } else {
          editor.chain().toggleOrderedList().focus(undefined, { scrollIntoView: false }).run();
        }
        return;
      }

      if (markdown === '> ') {
        if (selectedText) {
          editor
            .chain()
            .deleteRange({ from, to })
            .insertContent({
              type: 'blockquote',
              content: [{ type: 'paragraph', content: [{ type: 'text', text: selectedText }] }],
            })
            .focus(undefined, { scrollIntoView: false })
            .run();
        } else {
          editor.chain().toggleBlockquote().focus(undefined, { scrollIntoView: false }).run();
        }
        return;
      }

      if (markdown === '```\n\n```') {
        if (selectedText) {
          editor
            .chain()
            .deleteRange({ from, to })
            .insertContent({
              type: 'codeBlock',
              content: [{ type: 'text', text: selectedText }],
            })
            .focus(undefined, { scrollIntoView: false })
            .run();
        } else {
          editor.chain().toggleCodeBlock().focus(undefined, { scrollIntoView: false }).run();
        }
        return;
      }

      let insertText = markdown;

      if (selectedText && !markdown.match(/^(```|#|>|-|\d+\.)\s?/)) {
        insertText = markdown;
      }

      logger.debug(`📝 Final insert text: "${insertText}"`);

      try {
        // Capture initial positions before async operations
        const insertFrom = from;
        const insertTo = to;

        logger.debug('🔍 Checking formatting for text:', insertText);

        const formatChecks = {
          bold: insertText.includes('**'),
          italic: insertText.includes('*') && !insertText.includes('**'),
          strikethrough: insertText.includes('~~'),
          code: insertText.includes('`'),
          blockquote: insertText.startsWith('> '),
          bulletList: insertText.startsWith('- '),
          orderedList: /^\d+\.\s/.test(insertText),
          link: insertText.includes('[') && insertText.includes('](') && insertText.includes(')'),
          image: insertText.includes('![') && insertText.includes('](') && insertText.includes(')'),
          table:
            insertText.includes('|') && insertText.includes('\n') && insertText.includes('---'),
          heading: insertText.startsWith('#'),
        };

        logger.debug('🔍 Format checks:', formatChecks);

        const hasFormatting =
          formatChecks.bold ||
          formatChecks.italic ||
          formatChecks.strikethrough ||
          formatChecks.code ||
          formatChecks.blockquote ||
          formatChecks.bulletList ||
          formatChecks.orderedList ||
          formatChecks.link ||
          formatChecks.image ||
          formatChecks.table ||
          formatChecks.heading;

        logger.debug('🔍 Has formatting:', hasFormatting);

        if (hasFormatting) {
          logger.debug(`🔄 Converting to JSON for formatting: "${insertText}"`);

          setIsUpdating(true);

          const originalPreventUpdate = editor.__preventUpdate;
          editor.__preventUpdate = true;

          try {
            if (insertText.includes('[') && insertText.includes('](') && insertText.includes(')')) {
              const linkMatch = insertText.match(/\[([^\]]+)\]\(([^)]+)\)/);
              if (linkMatch) {
                const linkUrl = linkMatch[2];
                // Relaxed: we now allow domains without protocols, normalization will handle it
                if (linkUrl && !isValidUrl(linkUrl)) {
                  logger.warn('⚠️ Invalid URL detected, treating as plain text:', linkUrl);
                  insertPlainText(insertText);
                  return;
                }
                logger.debug('✅ Valid or domain-like URL detected, proceeding with link creation');
              }
            }

            const markdownJson = await MarkdownTipTapConverter.markdownToTipTapJson(insertText, {
              publicImagePathPrefix,
            });
            logger.debug('📄 Converted JSON - nodes:', markdownJson?.content?.length || 0);

            if (markdownJson.content && markdownJson.content.length > 0) {
              logger.debug('📄 Inserting content - nodes:', markdownJson.content.length);

              let contentToInsert = markdownJson.content;

              // If it's a single paragraph, unwrap it to insert its inline content
              // This ensures links are inserted inline instead of creating a new paragraph
              if (
                markdownJson.content.length === 1 &&
                markdownJson.content[0].type === 'paragraph' &&
                markdownJson.content[0].content
              ) {
                logger.debug('📄 Unwrapping single paragraph for inline insertion');
                contentToInsert = markdownJson.content[0].content;
              }

              // Use atomic replacement with captured positions
              const insertSuccess = editor
                .chain()
                .focus()
                .insertContentAt({ from: insertFrom, to: insertTo }, contentToInsert)
                .run();

              logger.debug('📄 Atomic JSON insert success:', insertSuccess);

              if (!insertSuccess) {
                const normalized = MarkdownTipTapConverter.tipTapJsonToMarkdown(markdownJson);
                insertPlainText(normalized);
              }

              logger.info('✅ Content inserted successfully');
            } else {
              logger.warn('⚠️ JSON conversion failed, inserting as plain text');
              insertPlainText(insertText);
            }
          } finally {
            setTimeout(() => {
              editor.__preventUpdate = originalPreventUpdate;
              setIsUpdating(false);
              logger.debug('✅ Markdown insert update locks released');
            }, UPDATE_LOCK_RELEASE_MS);
          }
        } else {
          // Plain text case: still use atomic replacement if there's a selection
          if (insertFrom !== insertTo) {
            editor
              .chain()
              .focus()
              .insertContentAt({ from: insertFrom, to: insertTo }, insertText)
              .run();
          } else {
            insertPlainText(insertText);
          }

          if (cursorOffset !== undefined && cursorOffset > 0) {
            const currentPos = editor.state.selection.from;
            const newPos = currentPos - (insertText.length - cursorOffset);
            editor.commands.setTextSelection(newPos);
          }
        }

        editor.commands.focus();
      } catch (error) {
        logger.error('❌ Error inserting markdown:', error);
        insertPlainText(insertText);
        editor.commands.focus();
      }
    },
    [editor, insertPlainText, publicImagePathPrefix, setIsUpdating],
  );

  return { handleInsertMarkdown };
};
